import { NODE_ENV } from '@config/env';
import { Images } from '@config/images';
import type { TConfigContext } from '@context/ConfigContext';
import { DEFAULT_CHAT_WINDOW_FONT_FAMILY } from '@conversed/shared/src/central/types/webclient-config.type';
import {
  resolveLocalizedText,
  type TLocalizedText,
} from '@conversed/shared/src/helpers/localized-text.helpers';
import type { Config } from '@type/configs';

// `activeLanguage` is the language the visitor picked in the flow. It falls back
// to the config's own `language`, which the admin sets to the bot default.
export const configToContext = (
  config: Config,
  activeLanguage: string,
): TConfigContext => {
  const {
    scheme,
    options,
    options: {
      showWelcomeAsFirstMessage,
      greeting,
      icons,
      isSoundEnabled,
      revisionId,
      defaultState,
      fonts,
    },
    botId = '',
    configId,
  } = config;

  const defaultLanguage = options.language || '';
  const resolve = (text: TLocalizedText | undefined) =>
    resolveLocalizedText(text, activeLanguage, defaultLanguage);

  return {
    showWelcomeAsFirstMessage: showWelcomeAsFirstMessage ?? true,
    popupTimeout: (options.popupTimeout || 2) * 1000,
    welcomeMessage: resolve(options.welcomeMessage),
    waitingForTheBotResponseMessage: resolve(
      options.waitingForTheBotResponseMessage,
    ),
    enableWaitingForBotResponseBlocker:
      options.enableWaitingForBotResponseBlocker,
    notificationAIGeneratedAnswer: resolve(
      options.notificationAIGeneratedAnswer,
    ),
    messageIcon: icons.speechIcon || '',
    messagePlaceholder: resolve(options.messagePlaceholder),
    menuTemplate: (options.menuTemplate || []).map((menuEntry) => ({
      ...menuEntry,
      title: resolve(menuEntry.title),
    })),
    refParam: options.refParam,
    revisionId,
    useAttachment: options.useAttachment || false,
    language: activeLanguage,
    isSoundEnabled: isSoundEnabled ?? true,
    bubbleProps: {
      dim: 48,
      icon: config && icons?.chatIcon ? icons.chatIcon : Images.bubbleIcon,
    },
    blockerConfig: {
      text:
        resolve(options?.blocker?.text) ||
        'Updating conversation in another tab',
      buttonText: resolve(options?.blocker?.buttonText) || 'Continue here',
    },
    headerConfig: {
      title: resolve(options.title),
      subtitle: resolve(options.subtitle),
      logoType: icons?.logo?.type ?? 'circle',
      logo: icons?.logo?.path || Images.cvLogo,
      headerType: options.headerType || 'normal',
    },
    scheme,
    greeting: {
      message: resolve(options.welcomeMessage),
      title: resolve(options.title),
      subtitle: resolve(options.subtitle),
      messageIcon: icons.speechIcon,
      avatarType: icons?.logo?.type ?? 'circle',
      avatarPath:
        icons?.logo?.path || `${process.env.PUBLIC_URL}${Images.cvLogo}`,
      headerType: options.headerType || 'normal',
      showCloseButton: options.showCloseButton !== false,
      ...greeting,
      buttonText: resolve(greeting?.buttonText),
    },
    botId: NODE_ENV !== 'production' ? 'local' : botId,
    configId,
    defaultState: defaultState || 'closed',
    requireMicrosoftLogin: options.requireMicrosoftLogin || false,
    fonts: {
      chatWindow: fonts?.chatWindow || DEFAULT_CHAT_WINDOW_FONT_FAMILY,
    },
    voice: options.voice,
    useStreamingResponse: options.useStreamingResponse ?? false,
  };
};

// Owns the language the visitor picked, so every context consumer re-renders
// when it changes. Seeded from storage first: on reload that repaints in the
// right language immediately, instead of flashing the default until core
// reports the session language again.
